type CustomerPaymentPolicyInput = {
  paymentPolicyType?: string | null;
  paymentPolicyLabel?: string | null;
  creditStatus?: string | null;
  requiresPrepayment?: boolean | null;
  bookingBlockMode?: string | null;
  paymentPolicyNotes?: string | null;
  paymentWarningLabel?: string | null;
  paymentBadgeTone?: 'GREEN' | 'RED' | 'ORANGE' | 'GRAY' | string | null;
  paymentBadgeLabel?: string | null;
};

export type CustomerPaymentBadge = {
  tone: 'GREEN' | 'RED' | 'ORANGE' | 'GRAY';
  label: string;
  warningLabel: string;
  bookingBlockMode: string;
  paymentPolicyType: string;
  paymentPolicyLabel: string | null;
  creditStatus: string;
  requiresPrepayment: boolean;
  paymentPolicyNotes: string | null;
};

export const getCustomerPaymentBadge = (
  input?: CustomerPaymentPolicyInput | null
): CustomerPaymentBadge => {
  const paymentPolicyType = String(input?.paymentPolicyType || '').trim().toUpperCase() || 'UNKNOWN';
  const paymentPolicyLabel = String(input?.paymentPolicyLabel || '').trim() || null;
  const creditStatus = String(input?.creditStatus || '').trim().toUpperCase() || 'UNKNOWN';
  const requiresPrepayment =
    input?.requiresPrepayment === true || paymentPolicyType === 'PREPAYMENT_REQUIRED';
  const bookingBlockMode = String(input?.bookingBlockMode || '').trim().toUpperCase() || 'NONE';
  const paymentPolicyNotes = String(input?.paymentPolicyNotes || '').trim() || null;
  const explicitTone = String(input?.paymentBadgeTone || '').trim().toUpperCase();
  const explicitLabel = String(input?.paymentBadgeLabel || '').trim();
  const explicitWarning = String(input?.paymentWarningLabel || '').trim();

  const fallback = (() => {
    if (creditStatus === 'BLOCKED') return { tone: 'ORANGE' as const, label: 'CREDIT BLOCKED', warning: 'CREDIT BLOCKED' };
    if (creditStatus === 'ON_HOLD') return { tone: 'ORANGE' as const, label: 'CREDIT HOLD', warning: 'CREDIT HOLD' };
    if (requiresPrepayment) return { tone: 'RED' as const, label: 'PREPAYMENT REQUIRED', warning: 'PREPAYMENT REQUIRED' };
    if (paymentPolicyType === 'UNKNOWN') {
      return { tone: 'GRAY' as const, label: 'UNKNOWN PAYMENT POLICY', warning: 'UNKNOWN PAYMENT POLICY / FINANCE REVIEW NEEDED' };
    }
    return { tone: 'GREEN' as const, label: 'CREDIT', warning: paymentPolicyLabel || 'CREDIT' };
  })();

  const tone =
    explicitTone === 'GREEN' || explicitTone === 'RED' || explicitTone === 'ORANGE'
      ? explicitTone
      : fallback.tone;

  return {
    tone,
    label: explicitLabel || fallback.label,
    warningLabel: explicitWarning || fallback.warning,
    bookingBlockMode,
    paymentPolicyType,
    paymentPolicyLabel,
    creditStatus,
    requiresPrepayment,
    paymentPolicyNotes,
  };
};

export const getCustomerPaymentBadgeClasses = (tone: CustomerPaymentBadge['tone']): string => {
  if (tone === 'GREEN') return 'bg-emerald-50 text-emerald-700 border border-emerald-200';
  if (tone === 'RED') return 'bg-rose-50 text-rose-700 border border-rose-200';
  if (tone === 'ORANGE') return 'bg-amber-50 text-amber-700 border border-amber-200';
  return 'bg-slate-100 text-slate-600 border border-slate-200';
};
